/**
 * Loads practitioners in scope and resolves calendar access for the current user.
 * Viewing other calendars follows role (admin / practice admin / supervisor).
 * Scheduling on another practitioner's calendar is deferred: `canManage` is
 * true only for the viewer.
 */
import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import type { Role } from '../../lib/rbacRoles';
import {
  calendarSessionTypeScopeFromRoles,
  formatCalendarPractitionerName,
  organizationIdFromPractitioner,
  resolveCalendarPractitionerAccess,
  rolesGrantPracticeCalendarAdmin,
  rolesGrantUnifyCalendarAdmin,
  type CalendarPractitionerAccess,
  type CalendarSessionTypeScope,
} from './calendarAccess';

export interface UseCalendarPractitionerAccessResult {
  loading: boolean;
  currentPractitioner: Practitioner | null;
  currentPractitionerId: string | null;
  currentOrganizationId: string | undefined;
  access: CalendarPractitionerAccess[];
  accessById: Map<string, CalendarPractitionerAccess>;
  canManageByOwnerId: Map<string, boolean>;
  /** True when the selector should be shown (more than self). */
  showPractitionerSelector: boolean;
  /** True when the user's roles grant platform-wide (cross-practice) calendar admin. */
  isUnifyAdmin: boolean;
  /** Session-type dropdown scope for this viewer. */
  sessionTypeScope: CalendarSessionTypeScope;
}

/** Page size for roster paging; every page is fetched so the selector is never truncated. */
export const CALENDAR_ROSTER_PAGE_SIZE = 100;

/**
 * Deliberately no `active=true`: FHIR token search does not match a missing
 * element, so filtering here hides practitioners whose optional `active` field
 * was never stamped. `resolveCalendarPractitionerAccess` drops `active: false`.
 */
export function calendarRosterSearchParams(offset: number): Record<string, string> {
  return {
    _count: String(CALENDAR_ROSTER_PAGE_SIZE),
    _sort: 'name',
    _offset: String(offset),
  };
}

export function filterCalendarRosterCandidates(
  results: readonly Practitioner[],
  opts: {
    unifyAdmin: boolean;
    practiceAdmin: boolean;
    supervisor?: boolean;
    organizationId?: string;
  },
): Practitioner[] {
  if (opts.unifyAdmin) return [...results];
  if (opts.practiceAdmin && opts.organizationId && !opts.supervisor) {
    return results.filter((p) => organizationIdFromPractitioner(p) === opts.organizationId);
  }
  // Supervisor (including practice-admin + supervisor): return roster; resolver applies scope.
  return [...results];
}

/** View others, but do not schedule as them until that feature returns. */
export function restrictScheduleManageToSelf(
  access: readonly CalendarPractitionerAccess[],
  selfId: string,
): CalendarPractitionerAccess[] {
  return access.map((entry) =>
    entry.practitionerId === selfId ? entry : { ...entry, canManage: false },
  );
}

async function searchPractitionersForCalendar(
  medplum: ReturnType<typeof useMedplum>,
  opts: {
    unifyAdmin: boolean;
    practiceAdmin: boolean;
    supervisor?: boolean;
    organizationId?: string;
  },
): Promise<Practitioner[]> {
  const results: Practitioner[] = [];
  let page: Practitioner[];
  let offset = 0;
  do {
    page = await medplum.searchResources('Practitioner', calendarRosterSearchParams(offset));
    results.push(...page);
    offset += CALENDAR_ROSTER_PAGE_SIZE;
  } while (page.length === CALENDAR_ROSTER_PAGE_SIZE);

  return filterCalendarRosterCandidates(results, opts);
}

export function useCalendarPractitionerAccess(): UseCalendarPractitionerAccessResult {
  const medplum = useMedplum();
  const { role, availableRoles } = usePermissions();
  const { practitioner: currentPractitioner, loading: practitionerLoading } =
    useCurrentUserPractitioner();

  const roles = useMemo(() => {
    const set = new Set<Role>();
    if (role) set.add(role as Role);
    for (const r of availableRoles ?? []) set.add(r as Role);
    return [...set];
  }, [role, availableRoles]);

  const isUnifyAdmin = useMemo(() => rolesGrantUnifyCalendarAdmin(roles), [roles]);
  const sessionTypeScope = useMemo(() => calendarSessionTypeScopeFromRoles(roles), [roles]);

  const [candidates, setCandidates] = useState<Practitioner[]>([]);
  const [loadingRoster, setLoadingRoster] = useState(true);

  const currentOrganizationId = currentPractitioner
    ? organizationIdFromPractitioner(currentPractitioner)
    : undefined;

  useEffect(() => {
    if (!currentPractitioner?.id) {
      setCandidates([]);
      setLoadingRoster(false);
      return;
    }

    let cancelled = false;
    setLoadingRoster(true);
    const unifyAdmin = isUnifyAdmin;
    const practiceAdmin = rolesGrantPracticeCalendarAdmin(roles);
    const supervisor = roles.includes('supervisor');

    // Provider-only: no roster needed beyond self.
    if (!unifyAdmin && !practiceAdmin && !supervisor) {
      setCandidates([currentPractitioner]);
      setLoadingRoster(false);
      return;
    }

    searchPractitionersForCalendar(medplum, {
      unifyAdmin,
      practiceAdmin,
      supervisor,
      organizationId: currentOrganizationId,
    })
      .then((list) => {
        if (cancelled) return;
        const byId = new Map<string, Practitioner>();
        byId.set(currentPractitioner.id!, currentPractitioner);
        for (const p of list) {
          if (p.id) byId.set(p.id, p);
        }
        setCandidates([...byId.values()]);
      })
      .catch((err) => {
        console.warn('[useCalendarPractitionerAccess] roster load failed:', err);
        if (!cancelled) setCandidates([currentPractitioner]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRoster(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, currentPractitioner, roles, currentOrganizationId, isUnifyAdmin]);

  const access = useMemo(() => {
    if (!currentPractitioner?.id) return [];
    return restrictScheduleManageToSelf(
      resolveCalendarPractitionerAccess({
        currentPractitioner,
        roles,
        candidates,
        currentOrganizationId,
      }),
      currentPractitioner.id,
    );
  }, [currentPractitioner, roles, candidates, currentOrganizationId]);

  const accessById = useMemo(
    () => new Map(access.map((a) => [a.practitionerId, a])),
    [access],
  );

  const canManageByOwnerId = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const a of access) map.set(a.practitionerId, a.canManage);
    return map;
  }, [access]);

  return {
    loading: practitionerLoading || loadingRoster,
    currentPractitioner,
    currentPractitionerId: currentPractitioner?.id ?? null,
    currentOrganizationId,
    access,
    accessById,
    canManageByOwnerId,
    showPractitionerSelector: access.length > 1,
    isUnifyAdmin,
    sessionTypeScope,
  };
}

export { formatCalendarPractitionerName };
